import type {
  EmailCodeFactor,
  PasswordFactor,
  ResetPasswordEmailCodeFactor,
  SignInFutureResource,
} from '@clerk/shared/types';
import { match } from 'ts-pattern';

import type { FirstFactorStrategy, SecondFactorStrategy } from './type';

type PrepareFirstFactorParams = {
  strategy: FirstFactorStrategy;
  signIn: SignInFutureResource;
};

export const prepareFirstFactor = ({ strategy, signIn }: PrepareFirstFactorParams) => {
  const supportedFirstFactors = signIn.supportedFirstFactors;
  return match(strategy)
    .with('password', () => {
      // Filter the returned array to find the 'password' entry
      const passwordFactor = supportedFirstFactors?.find((factor): factor is PasswordFactor => {
        return factor.strategy === 'password';
      });
      if (!passwordFactor) throw new Error('Password strategy not supported');
    })
    .with('email_code', async () => {
      // Filter the returned array to find the 'email_code' entry
      const emailCodeFactor = supportedFirstFactors?.find((factor): factor is EmailCodeFactor => {
        return factor.strategy === 'email_code';
      });
      if (!emailCodeFactor) throw new Error('Email code strategy not supported');

      const { emailAddressId } = emailCodeFactor;
      // Send the OTP code to the user
      await signIn.emailCode.sendCode({ emailAddressId });
    })
    .with('reset_password_email_code', async () => {
      // Filter the returned array to find the 'email_code' entry
      const resetPasswordEmailCodeFactor = supportedFirstFactors?.find(
        (factor): factor is ResetPasswordEmailCodeFactor => {
          return factor.strategy === 'reset_password_email_code';
        },
      );
      if (!resetPasswordEmailCodeFactor) throw new Error('Email code strategy not supported');

      // Send the OTP code to the user
      await signIn.resetPasswordEmailCode.sendCode();
    })
    .otherwise(() => {
      throw new Error(`Unsupported strategy: ${strategy}`);
    });
};

type PrepareSecondFactorParams = {
  strategy: SecondFactorStrategy;
  signIn: SignInFutureResource;
};

export const prepareSecondFactor = ({ strategy, signIn }: PrepareSecondFactorParams) => {
  const supportedSecondFactors = signIn.supportedSecondFactors;
  return match(strategy)
    .with('email_code', async () => {
      // Filter the returned array to find the 'email_code' entry
      const emailCodeFactor = supportedSecondFactors?.find(
        (factor): factor is EmailCodeFactor => factor.strategy === 'email_code',
      );
      if (!emailCodeFactor) throw new Error('Email code strategy not supported');

      // Send the OTP code to the user
      await signIn.mfa.sendEmailCode();
    })
    .otherwise(() => {
      throw new Error(`Unsupported strategy: ${strategy}`);
    });
};
